import React, { useState } from 'react';
import {
    Table,
    Button,
    Form,
    Input,
    InputNumber,
    Select,
    Typography,
    Space,
    Statistic,
    Divider
} from 'antd';

const { Title, Text } = Typography;
const { Option } = Select;

const PositionContractExpensesTable = () => {
    const [form] = Form.useForm();
    const [expenses, setExpenses] = useState([
        { id: 1, description: 'Onboarding', clientFees: 'Billable To Client', talentFees: 'Talent Cost', amount: 1500 },
        { id: 2, description: 'Supplies for events', clientFees: 'Billable To Client', talentFees: 'Reimbursable to talent', amount: 800 },
        { id: 3, description: 'Talent Benefits', clientFees: 'Non-Billable to Client', talentFees: 'Talent Cost', amount: 350 },
        { id: 4, description: 'Travel expenses to events', clientFees: 'Non-Billable to Client', talentFees: 'Reimbursable to talent', amount: 600 }
    ]);

    const [showForm, setShowForm] = useState(false);

    // Calculate totals and stats
    const totalAmount = expenses.reduce((sum, expense) => sum + expense.amount, 0);

    const nonBillableExpenses = expenses
        .filter(expense => expense.clientFees === 'Non-Billable to Client')
        .reduce((sum, expense) => sum + expense.amount, 0);

    const nonBillablePercentage = totalAmount > 0 ?
        ((nonBillableExpenses / totalAmount) * 100).toFixed(2) : 0;

    const talentReimbursable = expenses
        .filter(expense => expense.talentFees === 'Reimbursable to talent')
        .reduce((sum, expense) => sum + expense.amount, 0);

    const talentTakeRate = totalAmount > 0 ?
        ((talentReimbursable / totalAmount) * 100).toFixed(2) : 0;

    // Table columns definition
    const columns = [
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
        },
        {
            // title: 'Client Fees',
            dataIndex: 'clientFees',
            key: 'clientFees',
            render: (text) => {
                const color = text === 'Billable To Client' ? 'green' : 'red';
                return (
                    <Text style={{ color }}>
                        {text}
                    </Text>
                );
            },
        },
        {
            // title: 'Talent Fees',
            dataIndex: 'talentFees',
            key: 'talentFees',
            render: (text) => {
                const color = text === 'Reimbursable to talent' ? 'blue' : 'purple';
                return (
                    <Text style={{ color }}>
                        {text}
                    </Text>
                );
            },
        },
        {
            title: 'Amount',
            dataIndex: 'amount',
            key: 'amount',
            align: 'right',
            render: (text) => `$${text.toFixed(2)}`,
        },
    ];

    // Add a summary row to display the total
    const summary = (pageData) => {
        const total = pageData.reduce((sum, { amount }) => sum + amount, 0);

        return (
            <>
                <Table.Summary.Row>
                    <Table.Summary.Cell index={0} colSpan={3} align="right">
                        <strong>Total Expenses</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={1} align="right">
                        <strong>${total.toFixed(2)}</strong>
                    </Table.Summary.Cell>
                </Table.Summary.Row>
            </>
        );
    };

    const handleFinish = (values) => {
        const newExpense = {
            id: expenses.length > 0 ? Math.max(...expenses.map(e => e.id)) + 1 : 1,
            ...values,
        };

        setExpenses([...expenses, newExpense]);
        setShowForm(false);
        form.resetFields();
    };

    const handleCancel = () => {
        setShowForm(false);
        form.resetFields();
    };

    return (
        <div className="flex flex-col items-stretch gap-4">
            <div style={{ display: 'flex', gap: '24px' }}>
                <div style={{ flex: '3', marginRight: '16px' }}>
                    <div style={{
                        backgroundColor: 'white',
                        padding: '20px',
                        borderRadius: '8px',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                            <Title level={4}>Expenses</Title>
                            {!showForm && (
                                <Button type="primary" onClick={() => setShowForm(true)}>
                                    Add Expense
                                </Button>
                            )}
                        </div>

                        {showForm && (
                            <div style={{
                                backgroundColor: '#f9f9f9',
                                padding: '16px',
                                marginBottom: '16px',
                                borderRadius: '4px',
                                border: '1px solid #f0f0f0'
                            }}>
                                <div style={{ marginBottom: '16px' }}>
                                    <Title level={5}>New Expense</Title>
                                </div>
                                <Form
                                    form={form}
                                    name="expenseForm"
                                    layout="vertical"
                                    onFinish={handleFinish}
                                    initialValues={{
                                        clientFees: 'Billable To Client',
                                        talentFees: 'Talent Cost',
                                    }}
                                >
                                    <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                                        <div style={{ flex: '1', minWidth: '200px' }}>
                                            <Form.Item
                                                name="description"
                                                label="Description"
                                                rules={[{ required: true, message: 'Description is required' }]}
                                            >
                                                <Input.TextArea rows={3} placeholder="Enter description" />
                                            </Form.Item>
                                        </div>
                                        <div style={{ flex: '1', minWidth: '200px' }}>
                                            <Form.Item
                                                name="clientFees"
                                                label="Client Fees"
                                                rules={[{ required: true, message: 'Client fees is required' }]}
                                            >
                                                <Select>
                                                    <Option value="Billable To Client">Billable To Client</Option>
                                                    <Option value="Non-Billable to Client">Non-Billable to Client</Option>
                                                </Select>
                                            </Form.Item>
                                        </div>
                                        <div style={{ flex: '1', minWidth: '200px' }}>
                                            <Form.Item
                                                name="talentFees"
                                                label="Talent Fees"
                                                rules={[{ required: true, message: 'Talent fees is required' }]}
                                            >
                                                <Select>
                                                    <Option value="Reimbursable to talent">Reimbursable to talent</Option>
                                                    <Option value="Talent Cost">Talent Cost</Option>
                                                </Select>
                                            </Form.Item>
                                        </div>
                                        <div style={{ flex: '1', minWidth: '200px' }}>
                                            <Form.Item
                                                name="amount"
                                                label="Amount"
                                                rules={[{ required: true, message: 'Amount is required' }]}
                                            >
                                                <InputNumber
                                                    style={{ width: '100%' }}
                                                    min={0}
                                                    step={0.01}
                                                    precision={2}
                                                    formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                                    parser={value => value.replace(/\$\s?|(,*)/g, '')}
                                                    placeholder="0.00"
                                                />
                                            </Form.Item>
                                        </div>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                        <Space>
                                            <Button onClick={handleCancel}>Cancel</Button>
                                            <Button type="primary" htmlType="submit">Submit</Button>
                                        </Space>
                                    </div>
                                </Form>
                            </div>
                        )}

                        <Table
                            columns={columns}
                            dataSource={expenses}
                            rowKey="id"
                            pagination={false}
                            summary={summary}
                        />
                    </div>
                </div>

                <div className="flex-1 flex flex-col items-stretch justify-center h-96">
                    <div style={{
                        backgroundColor: 'white',
                        padding: '20px',
                        borderRadius: '8px',
                        boxShadow: '0 2px 3px rgba(0,0,0,0.3)'
                    }}>
                        <Statistic
                            title="Non-Billable Expenses"
                            valueStyle={{color: TB.config('color.danger')}}
                            value={nonBillableExpenses}
                            precision={2}
                            prefix="$"
                        />

                    </div>
                    <Divider />

                    <div style={{
                        backgroundColor: 'white',
                        padding: '20px',
                        borderRadius: '8px',
                        boxShadow: '0 2px 3px rgba(0,0,0,0.3)'
                    }}>
                        <Statistic
                            title="Take Rate"
                            value={talentTakeRate}
                            precision={2}
                            suffix="%"
                        />
                    </div>
                </div>
            </div>

            <div className="flex flex-row items-center gap-8 justify-start">
                <div className="flex-w flex flex-row items-center gap-3">
                    <p className="text-base font-semibold whitespace-nowrap">
                        Total Client Contract Value:
                    </p>
                    <div className="bg-purple-100 px-4 py-3 flex flex-col items-center justify-center">
                        <p className="text-secondary font-semibold text-center">
                            $153,334.00
                        </p>
                    </div>
                </div>
                <div className="flex-w flex flex-row items-center gap-3">
                    <p className="text-base font-semibold whitespace-nowrap">
                        Fees:
                    </p>
                    <div className="bg-gray-100 px-4 py-3 flex flex-col items-center justify-center">
                        <p className="font-semibold text-center">
                            $151,234.00
                        </p>
                    </div>
                </div>
                <div className="flex-w flex flex-row items-center gap-3">
                    <p className="text-base font-semibold whitespace-nowrap">
                        Billable Expenses:
                    </p>
                    <div className="bg-gray-100 px-4 py-3 flex flex-col items-center justify-center">
                        <p className="font-semibold text-center">
                            $2,300.00
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PositionContractExpensesTable;